import { CaretIcon, EmptyIcon } from '@/assets/icons';
import { TableLinkTo } from '@/components';
import { useDetailListSubmit } from '@/hooks';
import { cn, insertObjectIf, insertObjectIfV2 } from '@/utils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ConfigProvider, Table as TableAntd } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import { ColumnTitleProps, SortOrder } from 'antd/es/table/interface';

import { includes, remove } from 'ramda';
import {
	isNilOrEmpty,
	isNotNilOrEmpty,
	isNotUndefined,
	isUndefined,
} from 'ramda-adjunct';
import type { GetComponentProps } from 'rc-table/lib/interface';

import { NextIcon, PrevIcon } from '../pagination';
import {
	EditableCell,
	EditableRow,
	EditableRowRef,
	ResizableCell,
} from './components/editable';
import { getRenderDropdown, getRenderTextInput } from './config';
import { ColumnTypes, EditableCellType, TableProps } from './type';

const Table = ({
	pagination,
	columns,
	rowSelection,
	editable = false,
	resizable,
	allowSelectCheckboxOnRow,
	toDetailPathName,
	handleOnRow,
	searchFilterRef,
	idKeyToDetail = '',
	editableRowRefs,
	isSearching,
	mergeHeader,
	settingListFields,
	needToReloadListAfterClosePopup,
	showAllSorter,
	callbackAfterClosePopup,
	toDetailQuery,
	expandable,
	...rest
}: TableProps) => {
	const { pathname } = useLocation();

	const { t } = useTranslation('common');

	const rowKey = rest?.rowKey as string;

	const { onDetailSubmit } = useDetailListSubmit({
		toDetailPathName,
		searchFilterRef,
		callbackAfterClosePopup,
	});

	const [columnsFormatted, setColumnsFormatted] = useState<
		ColumnTypes | undefined
	>(columns);

	const _columns = useMemo(() => {
		const mapColumns = (column: ColumnTypes[number], index: number) => {
			const newColumns = {
				...column,
				width: column.width || 'auto',
				...insertObjectIfV2(showAllSorter && isNilOrEmpty(column?.children), {
					sorter: isNotUndefined(column.sorter) ? column.sorter : true,
				}),
				...insertObjectIfV2(column?.required, {
					title: (props: ColumnTitleProps<any>) => {
						return (
							<span
								className={
									props?.sortColumns?.[0]?.column?.key === column?.key &&
									props?.sortColumns?.[0]?.order
										? 'text-primary'
										: ''
								}
							>
								{column?.title as any}
								{column?.required && <span className="text-error">*</span>}
							</span>
						);
					},
				}),
				...insertObjectIfV2(isUndefined(column.align), {
					align: 'center',
				}),
				...insertObjectIfV2(
					!!column?.sorter || (showAllSorter && isNilOrEmpty(column?.children)),
					{
						sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) => {
							return (
								<CaretIcon
									active={!!sortOrder}
									className={sortOrder === 'descend' ? 'rotate-180' : ''}
								/>
							);
						},
						title: (props: ColumnTitleProps<any>) => {
							return (
								<span
									className={
										props?.sortColumns?.[0]?.column?.key === column?.key &&
										props?.sortColumns?.[0]?.order
											? 'text-primary'
											: ''
									}
								>
									{column?.title as any}
									{column?.required && <span className="text-error">*</span>}
								</span>
							);
						},
					}
				),
				...insertObjectIfV2(resizable, {
					width: column.width || 192,
					onHeaderCell: (cell: ColumnTypes[number]) => ({
						width: cell.width,
						onResize: handleResize(column.dataIndex),
					}),
				}),
				...insertObjectIfV2(!expandable && isUndefined(column?.render), {
					render: (value: any, record: AnyObject, index: number) => {
						return getRenderTextInput(value, record, index, column);
					},
				}),
				...insertObjectIfV2(
					isNotNilOrEmpty(toDetailPathName) && column.key === rowKey,
					{
						className: cn('after:!w-[1px]', column.className),
						width: column.minWidth || 'auto',

						render: (id: string, record: any) => (
							<TableLinkTo
								label={id}
								onClick={(e) => {
									e?.stopPropagation();
									onDetailSubmit(
										record?.[idKeyToDetail] ?? id,
										toDetailPathName,
										toDetailQuery?.(record)
									);
								}}
								className="font-normal"
							/>
						),
					}
				),
				...insertObjectIfV2(
					!expandable &&
						(column as ColumnTypes[number]).cellType ===
							EditableCellType.DROPDOWN,
					{
						render: (value: string, record: AnyObject, index: number) => {
							return getRenderDropdown(value, record, index, column, t);
						},
					}
				),
			};

			if (column?.children) {
				newColumns.children = column.children.map(mapColumns) as ColumnTypes;
			}

			return newColumns;
		};

		return (columns as ColumnTypes)?.map(mapColumns) as ColumnTypes;
	}, [
		columns,
		rowKey,
		pathname,
		onDetailSubmit,
		idKeyToDetail,
		t,
		toDetailQuery,
		expandable,
	]);

	useEffect(() => {
		setColumnsFormatted(_columns);
	}, [_columns]);

	const handleResize = useCallback(
		(dataIndex: string) => (width: number) => {
			setColumnsFormatted((prevColumns) => {
				if (!prevColumns) return;
				const updateColumnWidth = (columns: any[]): any[] =>
					columns.map((col) => {
						if (col.dataIndex === dataIndex) {
							return { ...col, width };
						}
						if (col.children) {
							return { ...col, children: updateColumnWidth(col.children) };
						}
						return col;
					});

				return updateColumnWidth(prevColumns);
			});
		},
		[]
	);

	// Ref to track if dragging is in progress
	const isDragging = useRef(false);
	// Ref to store the initial mouse position
	const startPosition = useRef({ x: 0, y: 0 });

	const onRow: GetComponentProps<any> = useCallback(
		(record) => {
			return {
				onMouseDown: (ev) => {
					// Store the initial mouse position when the mouse button is pressed
					startPosition.current = { x: ev.clientX, y: ev.clientY };
					isDragging.current = false;
				},
				onMouseMove: (ev) => {
					// Calculate the distance moved by the mouse
					const distance = Math.sqrt(
						Math.pow(ev.clientX - startPosition.current.x, 2) +
							Math.pow(ev.clientY - startPosition.current.y, 2)
					);

					// Mark as dragging if the distance moved is greater than 3px
					if (distance > 3) {
						isDragging.current = true;
					}
				},
				onClick: (ev) => {
					// If the row is being dragged, prevent the default behavior
					if (isDragging.current) {
						return ev.preventDefault();
					}
					ev.preventDefault();
					if (allowSelectCheckboxOnRow && rowKey && !record?.disabled) {
						const selectedRowKeys = rowSelection?.selectedRowKeys || [];
						const foundIndex = selectedRowKeys?.findIndex?.(
							(row) => row === record[rowKey]
						);

						const newSelectedRowKeys =
							foundIndex > -1
								? remove(foundIndex, 1, selectedRowKeys)
								: [...(selectedRowKeys || []), record[rowKey]];

						rowSelection?.onChange?.(
							newSelectedRowKeys,
							(rest?.dataSource as [])?.filter((it) =>
								includes(it[rowKey], newSelectedRowKeys)
							),
							{ type: 'multiple' }
						);
					}
					handleOnRow?.(record);

					if (!toDetailPathName) return;

					const evTarget = ev.target as HTMLElement;

					const isGroupHeader =
						evTarget?.parentElement
							?.getAttribute('data-row-key')
							?.split?.('-')?.[0] === 'group';

					const firstElementChildClassNames =
						(evTarget?.firstElementChild as Element)?.className?.split?.(' ') ||
						[];
					if (
						includes('render-cell', firstElementChildClassNames) ||
						includes('editable-cell', firstElementChildClassNames) ||
						includes('ant-checkbox-wrapper', firstElementChildClassNames) ||
						isGroupHeader
					)
						return;

					onDetailSubmit(
						record?.[idKeyToDetail] ?? record[rowKey],
						toDetailPathName,
						toDetailQuery?.(record)
					);
				},
			};
		},
		[
			pathname,
			allowSelectCheckboxOnRow,
			rowKey,
			rest?.dataSource,
			rowSelection,
			handleOnRow,
			toDetailPathName,
			onDetailSubmit,
			idKeyToDetail,
			startPosition,
			isDragging,
			toDetailQuery,
		]
	);

	const components: TableProps['components'] = useMemo(() => {
		return {
			...insertObjectIfV2(resizable, {
				header: {
					cell: ResizableCell,
				},
			}),
			...insertObjectIfV2(editable, {
				body: {
					cell: EditableCell,
					row: (props: any) => {
						const rowRef = useRef<EditableRowRef>(null);
						if (editableRowRefs?.current)
							editableRowRefs.current[props['data-row-key']] = rowRef;

						return (
							<EditableRow
								{...props}
								ref={rowRef}
								validationSchema={
									typeof rest?.validationSchema === 'function'
										? rest?.validationSchema?.(settingListFields || [])
										: rest?.validationSchema
								}
								tableType={rest?.tableType}
								needToReloadListAfterClosePopup={
									needToReloadListAfterClosePopup
								}
							/>
						);
					},
				},
			}),
		};
	}, [
		resizable,
		editable,
		rest?.validationSchema,
		settingListFields,
		needToReloadListAfterClosePopup,
	]);

	return (
		<ConfigProvider
			theme={{
				components: {
					Table: {
						/* here is your component tokens */
						borderColor: '#D9D9D9',
						borderRadius: 0,
						headerBorderRadius: 0,
						headerBg: '#eff2ff',
						headerColor: '#333333',
						rowHoverBg: '#FCFCFC',
						rowSelectedBg: '#FCFCFC',
						rowSelectedHoverBg: '#FCFCFC',
						cellFontSize: 13,
						cellPaddingBlock: 0,
						cellPaddingInline: 12,
						lineHeight: 1.13,
						fontSize: 15,
						colorText: '#222222',
					},
					Pagination: {
						itemSize: 24,
						itemActiveBg: '#607EFE',
					},
				},
			}}
			renderEmpty={() => (
				<div className="flex flex-col justify-center items-center py-5 gap-2">
					<EmptyIcon />
					<p className="text-center text-font8">
						{searchFilterRef?.current?.isSearching || isSearching
							? t?.('no-search-result')
							: t?.('no-information')}
					</p>
				</div>
			)}
		>
			<TableAntd
				bordered
				pagination={
					pagination && {
						...pagination,
						showSizeChanger: false,
						size: 'default',
						prevIcon: PrevIcon,
						nextIcon: NextIcon,
						position: ['bottomCenter'],
					}
				}
				columns={columnsFormatted}
				onRow={onRow}
				{...insertObjectIf(!!rowSelection, {
					rowSelection: {
						...rowSelection,
						columnWidth: 34,
						...insertObjectIf(mergeHeader, {
							columnTitle: (checkboxNode: any) => {
								return (
									<div className="h-[68px] col justify-end">
										<div className="h-1/2 center relative after:border-gray1 after:border-t after:absolute after:left-0 after:-top-[1px] after:right-0">
											{checkboxNode}
										</div>
									</div>
								);
							},
						}),
					},
				})}
				rowKey={rowKey}
				components={components}
				style={{
					borderRadius: 0,
				}}
				{...rest}
			/>
		</ConfigProvider>
	);
};

export default memo(Table);
